// @ts-check
/** Walk-in leads captured at the rail (someone who tried a piece but did not buy): list, filter by
 * event, and export to CSV for follow-up outside the app. */
import { Download, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import adminWalkInLeadService from '../services/adminWalkInLeadService';
import { reportBulkResult, runBulk } from '../services/bulkService';
import { hasMeasurements } from '../utils/customerMeasurements';
import { buildLeadsCsv } from '../utils/walkInLead';
import BulkActionBar from './ui/BulkActionBar';
import BulkConfirmModal from './ui/BulkConfirmModal';
import { HeaderCheckbox, RowCheckbox } from './ui/RowCheckbox';
import WalkInLeadModal from './WalkInLeadModal';

const hasPreferrence = (lead) => {
  return lead.preferred_product_id !== null && lead.preferred_product_id !== undefined;
};

/** @param {{events: Array<{id: string, name: string, country_code?: string | null}>}} props */
const WalkInLeadsPanel = ({ events }) => {
  const toast = useToast();
  const [eventId, setEventId] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLead, setEditingLead] = useState(/** @type {any} */ (null));
  const [capturing, setCapturing] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(/** @type {'delete' | 'move' | null} */ (null));
  const [moveEventId, setMoveEventId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const selection = useRowSelection(
    leads.map((lead) => lead.id),
    { resetKey: eventId },
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLeads(await adminWalkInLeadService.list(eventId || undefined));
    } catch (err) {
      toast.error(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [eventId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete the lead for ${lead.name}? This cannot be undone.`)) return;
    try {
      await adminWalkInLeadService.remove(lead.id);
      toast.success('Lead deleted');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete the lead');
    }
  };

  const handleExport = (rows = leads) => {
    const blob = new Blob([buildLeadsCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kaay-walk-in-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const runBulkAction = async (action, params, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/walk-in-leads/bulk', { ids: selection.selectedIds, action, params });
      reportBulkResult(toast, result, verb, 'lead');
      setBulkDialog(null);
      selection.clear();
      load();
    } catch (err) {
      toast.error(err.message || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          className="px-3 py-2 text-sm border border-input rounded-lg"
        >
          <option value="">All events</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
        <button
          onClick={() => handleExport()}
          disabled={leads.length === 0}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
        <button
          onClick={() => setCapturing(true)}
          disabled={!eventId}
          title={eventId ? undefined : 'Choose an event above first - a lead is always captured for one'}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-40"
        >
          <Plus size={14} /> Capture a lead
        </button>
      </div>

      <BulkActionBar count={selection.count} onClear={selection.clear}>
        <BulkActionBar.Button onClick={() => handleExport(leads.filter((lead) => selection.isSelected(lead.id)))}>
          Export CSV
        </BulkActionBar.Button>
        <BulkActionBar.Button onClick={() => setBulkDialog('move')}>Move to event</BulkActionBar.Button>
        <BulkActionBar.Button tone="danger" onClick={() => setBulkDialog('delete')}>
          Delete
        </BulkActionBar.Button>
      </BulkActionBar>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : leads.length === 0 ? (
        <div className="bg-card border rounded-lg border-border p-12 text-center text-muted-foreground">
          No leads yet.
        </div>
      ) : (
        <div className="bg-card border rounded-lg border-border shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="pl-6 py-3">
                  <HeaderCheckbox selection={selection} />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  Preferred Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Style</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  Measurements
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-muted/50">
                  <td className="pl-6 py-4">
                    <RowCheckbox selection={selection} id={lead.id} label={`Select ${lead.name}`} />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">{lead.name}</td>
                  <td className="px-6 py-4 text-sm">{lead.phone}</td>
                  <td className="px-6 py-4 text-sm">
                    {hasPreferrence(lead) ? (
                      <span className="px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 text-xs">
                        Preferred
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">{lead.style || '—'}</td>
                  <td className="px-6 py-4 text-sm">{lead.size_tried || '—'}</td>
                  <td className="px-6 py-4 text-sm">
                    {hasMeasurements({ measurements: lead.measurements, fitNotes: lead.fit_notes }) ? (
                      <span className="px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 text-xs">
                        Measured
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{lead.notes || '—'}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-3">
                      <button
                        onClick={() => setEditingLead(lead)}
                        className="text-muted-foreground hover:text-foreground underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(lead)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bulkDialog === 'delete' && (
        <BulkConfirmModal
          title="Delete leads"
          message="They drop off the list. The rows are kept in the database, not removed."
          count={selection.count}
          confirmLabel="Delete"
          danger
          requireCount
          busy={bulkBusy}
          onConfirm={() => runBulkAction('delete', {}, 'deleted')}
          onClose={() => setBulkDialog(null)}
        />
      )}
      {bulkDialog === 'move' && (
        <BulkConfirmModal
          title="Move leads to another event"
          message="Leads already at the chosen event are skipped."
          count={selection.count}
          confirmLabel="Move"
          busy={bulkBusy}
          onConfirm={() => moveEventId && runBulkAction('move_to_event', { sales_event_id: moveEventId }, 'moved')}
          onClose={() => setBulkDialog(null)}
        >
          <select
            aria-label="Event"
            value={moveEventId}
            onChange={(e) => setMoveEventId(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Choose an event...</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </BulkConfirmModal>
      )}

      {editingLead && (
        <WalkInLeadModal
          salesEventId={editingLead.sales_event_id}
          eventCountry={events.find((e) => e.id === editingLead.sales_event_id)?.country_code}
          lead={editingLead}
          onClose={() => setEditingLead(null)}
          onSaved={() => {
            setEditingLead(null);
            load();
          }}
        />
      )}

      {capturing && (
        <WalkInLeadModal
          salesEventId={eventId}
          eventCountry={events.find((e) => e.id === eventId)?.country_code}
          onClose={() => setCapturing(false)}
          onSaved={() => {
            setCapturing(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default WalkInLeadsPanel;
